
import React from 'react';
import { Phone, Mail } from 'lucide-react';

interface TeamMemberCardProps {
  name: string;
  title: string;
  role: string;
  phone: string;
  email: string;
}

const TeamMemberCard: React.FC<TeamMemberCardProps> = ({ 
  name, 
  title, 
  role, 
  phone, 
  email 
}) => {
  // Determine which team photo to use based on the name
  const getTeamPhotoUrl = (memberName: string) => {
    if (memberName === 'Alen Mikic') {
      return 'https://smfuwrmdonlmchctxvjq.supabase.co/storage/v1/object/public/team-photos/Alen.png';
    } else if (memberName === 'Almir Sisic') {
      return 'https://smfuwrmdonlmchctxvjq.supabase.co/storage/v1/object/public/team-photos/Almir.png';
    }
    // Fallback to a professional placeholder
    return 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop&crop=face';
  };

  return (
    <div className="group relative h-full">
      {/* Enhanced Professional Team Card */}
      <div className="relative bg-white/10 backdrop-blur-md border border-white/20 rounded-3xl p-8 md:p-10 h-full transition-all duration-300 hover:scale-[1.02] hover:bg-white/15 hover:border-white/30 hover:shadow-elegant-lg">
        {/* Subtle Background Gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
        
        {/* Team Member Photo - Much Larger */}
        <div className="relative z-10 flex justify-center mb-8 md:mb-10">
          <div className="relative">
            {/* Enhanced Photo Container - Significantly Larger */}
            <div className="relative w-32 h-32 sm:w-36 sm:h-36 md:w-48 md:h-48 lg:w-52 lg:h-52 rounded-full overflow-hidden border-4 border-secondary-brand/40 group-hover:border-secondary-brand/70 transition-all duration-300 shadow-elegant-lg">
              <img
                src={getTeamPhotoUrl(name)}
                alt={name}
                className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-300"
                style={{ objectPosition: '50% 20%' }} // Prevents hair cropping by positioning face lower
              />
              {/* Subtle overlay for better contrast */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>
            
            {/* Enhanced glow effect */}
            <div className="absolute inset-0 rounded-full bg-secondary-brand/20 blur-xl opacity-0 group-hover:opacity-30 transition-opacity duration-300 -z-10"></div>
          </div>
        </div>
        
        {/* Enhanced Content */}
        <div className="relative z-10 text-center space-y-6">
          <div>
            <h3 className="text-2xl md:text-3xl font-bold text-white group-hover:text-secondary-brand transition-colors duration-300 leading-tight mb-3">
              {name}
            </h3>
            <p className="text-secondary-brand font-semibold text-lg md:text-xl mb-2">
              {title}
            </p>
            <p className="text-gray-300 text-base md:text-lg">
              {role}
            </p>
          </div>
          
          {/* Enhanced Contact Information */}
          <div className="space-y-4 pt-2">
            <div className="flex items-center justify-center gap-3 text-gray-300 hover:text-white transition-colors duration-300 group/contact">
              <Phone className="h-5 w-5 text-secondary-brand group-hover/contact:scale-110 transition-transform duration-300" />
              <a 
                href={`tel:${phone}`}
                className="text-base hover:text-secondary-brand transition-colors duration-300 font-medium"
              >
                {phone}
              </a>
            </div>
            <div className="flex items-center justify-center gap-3 text-gray-300 hover:text-white transition-colors duration-300 group/contact">
              <Mail className="h-5 w-5 text-secondary-brand group-hover/contact:scale-110 transition-transform duration-300" />
              <a 
                href={`mailto:${email}`}
                className="text-base hover:text-secondary-brand transition-colors duration-300 font-medium"
              >
                {email}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamMemberCard;
